import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  #buyButtonsIntersectionObserver = null;
  #mainBottomObserver = null;
  #resetTimeout = null;
  #isStuck = false;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtons = productForm.closest('.buy-buttons-block');
    if (!buyButtons) return;

    const footer =
      document.querySelector('footer') || document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (!entry) return;

      const rect = entry.target.getBoundingClientRect();

      if (!entry.isIntersecting && !this.#isStuck && (rect.bottom < 0 || rect.top < 0)) {
        this.#showStickyBar();
      }

      if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        }

        if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtons.getBoundingClientRect();

          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtons);
    this.#mainBottomObserver.observe(footer);

    this.#cacheAddButton(productForm);
  }

  #cacheAddButton(productForm) {
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = 'true';

    requestAnimationFrame(() => {
      this.#targetAddToCartButton.click();
    });

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    this.refs.addToCartButton.dataset.added = 'true';

    if (!cartIcon || !this.refs.productImage) return;

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const fly = document.createElement('fly-to-cart');
    fly.classList.add('fly-to-cart--sticky');
    fly.style.backgroundImage = `url(${this.refs.productImage.src})`;
    fly.source = this.refs.productImage;
    fly.destination = cartIcon;

    document.body.appendChild(fly);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton?.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = async event => {
    const variant = event.detail.resource;
    const newSticky = event.detail.data?.html?.querySelector?.('sticky-add-to-cart');
    if (!newSticky) return;

    const newBar = newSticky.querySelector('[ref="stickyBar"]');
    if (!newBar) return;

    // `morph` only patches inside stickyBar (childrenOnly: true), so it never
    // touches attributes on the root <sticky-add-to-cart> element itself.
    // The show/hide CSS depends on data-variant-available living on that
    // root element, so it has to be synced here explicitly.
    this.dataset.variantAvailable = String(newSticky.dataset.variantAvailable === 'true');

    // Let morph apply the fully server-rendered markup (price, availability,
    // variant title, etc).
    await morph(this.refs.stickyBar, newBar, { childrenOnly: true });

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#cacheAddButton(productForm);
    }

    if (variant?.price == null) {
      this.#handleVariantUnavailable();
      this.#updateButtonText();
      return;
    }

    this.dataset.currentVariantId = variant.id ?? this.dataset.currentVariantId;

    // Fallback in case the morphed markup didn't include an updated price
    // node (defensive only — normally morph already handled this).
    const priceElement = this.refs.stickyBar.querySelector('[ref="price"]');
    if (priceElement) {
      const formattedPrice = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: Shopify.currency.active,
      }).format(variant.price / 100);

      if (priceElement.textContent.trim() !== formattedPrice) {
        priceElement.textContent = formattedPrice;
      }

      priceElement.dataset.variantPrice = variant.price;
      this.refs.price = priceElement;
    }

    this.#updateButtonText();
  };

  #handleVariantSelected = event => {
    const id = event.detail.resource?.id;
    if (!id) return;

    this.dataset.currentVariantId = id;
  };

  #handleCartComplete = () => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  #handleQuantityUpdate = event => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #handleVariantUnavailable() {
    this.dataset.currentVariantId = '';

    const title = this.querySelector('.sticky-add-to-cart__variant');
    const picker = document.querySelector(
      `variant-picker[data-product-id="${this.dataset.productId}"]`
    );

    if (!title || !picker) return;

    const options = [...picker.querySelectorAll('input:checked')]
      .map(el => el.value)
      .filter(Boolean)
      .join(' / ');

    title.textContent = options || 'Unavailable';
  }

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  #getProductForm() {
    const id = this.dataset.productId;
    if (!id) return null;

    const section = this.closest('.shopify-section');
    if (!section) return null;

    const sectionId = section.id.replace('shopify-section-', '');

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${id}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = Number(this.dataset.initialQuantity || 1);
    this.#updateButtonText();
  }

  #updateButtonText() {
    const button = this.refs.addToCartButton;
    const display = this.refs.quantityDisplay;
    const number = this.refs.quantityNumber;

    if (!button || !display || !number) return;

    number.textContent = this.#currentQuantity;

    if (!button.disabled && this.#currentQuantity > 1) {
      display.style.display = 'inline';
    } else {
      display.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}